import { Card, Colors, Icon, Text } from '@amelio/ui-library';

import type { PriorityAttribute } from '@/types/priorityAttributeType';
import type { DraggableContainer, DraggableItem } from '@amelio/ui-library';

import {
    PriorityBoard,
    PRIORITY_1_CONTAINER_ID,
    PRIORITY_2_CONTAINER_ID,
    PRIORITY_3_CONTAINER_ID,
} from '@/components/PriorityBoard/PriorityBoard';
import { useAmelioTranslations } from '@/hooks';

const PRIORITY_BY_CONTAINER_ID: Record<string, 1 | 2 | 3> = {
    [PRIORITY_1_CONTAINER_ID]: 1,
    [PRIORITY_2_CONTAINER_ID]: 2,
    [PRIORITY_3_CONTAINER_ID]: 3,
};

interface PrioritySectionProps {
    priority1: DraggableItem<PriorityAttribute>[];
    priority2: DraggableItem<PriorityAttribute>[];
    priority3: DraggableItem<PriorityAttribute>[];
    isReadOnly?: boolean;
    onPriority1Change: (items: DraggableItem<PriorityAttribute>[]) => void;
    onPriority2Change: (items: DraggableItem<PriorityAttribute>[]) => void;
    onPriority3Change: (items: DraggableItem<PriorityAttribute>[]) => void;
    onAddAttribute?: (priority: 1 | 2 | 3) => void;
    onRemoveAttribute?: (priority: 1 | 2 | 3, itemId: string) => void;
    dataTestId?: string;
}

const PrioritySection = ({
    priority1,
    priority2,
    priority3,
    isReadOnly = false,
    onPriority1Change,
    onPriority2Change,
    onPriority3Change,
    onAddAttribute,
    onRemoveAttribute,
    dataTestId = 'priority-section',
}: PrioritySectionProps) => {
    const { t } = useAmelioTranslations();

    const handleChange = (containers: DraggableContainer<PriorityAttribute>[]) => {
        const changeHandlers: Record<string, (items: DraggableItem<PriorityAttribute>[]) => void> =
            {
                [PRIORITY_1_CONTAINER_ID]: onPriority1Change,
                [PRIORITY_2_CONTAINER_ID]: onPriority2Change,
                [PRIORITY_3_CONTAINER_ID]: onPriority3Change,
            };

        containers.forEach((container) => {
            const handler = changeHandlers[container.id];
            if (handler) {
                handler(container.items as DraggableItem<PriorityAttribute>[]);
            }
        });
    };

    const handleAddAttribute = (containerId: string) => {
        const priority = PRIORITY_BY_CONTAINER_ID[containerId];
        if (priority) {
            onAddAttribute?.(priority);
        }
    };

    const handleRemoveAttribute = (containerId: string, itemId: string) => {
        const priority = PRIORITY_BY_CONTAINER_ID[containerId];
        if (priority) {
            onRemoveAttribute?.(priority, itemId);
        }
    };

    const containerConfigs = [
        {
            id: PRIORITY_1_CONTAINER_ID,
            number: 1,
            label: t('cycleEvaluationSelectEvaluators.prioritySection.priority1Label'),
            sublabel: t('cycleEvaluationSelectEvaluators.prioritySection.priority1'),
            items: priority1,
        },
        {
            id: PRIORITY_2_CONTAINER_ID,
            number: 2,
            label: t('cycleEvaluationSelectEvaluators.prioritySection.priority2Label'),
            sublabel: t('cycleEvaluationSelectEvaluators.prioritySection.priority2'),
            items: priority2,
        },
        {
            id: PRIORITY_3_CONTAINER_ID,
            number: 3,
            label: t('cycleEvaluationSelectEvaluators.prioritySection.priority3Label'),
            sublabel: t('cycleEvaluationSelectEvaluators.prioritySection.priority3'),
            items: priority3,
        },
    ];

    return (
        <Card
            className={'mt-12 max-md:p-4 md:p-10'}
            padding={'none'}
            bgColor={'bg-white'}
            borderRadius={'2xl'}
            dataTestId={dataTestId}
        >
            <div className={'mb-5 flex items-start gap-6 sm:mb-8 md:mb-10'}>
                <Icon
                    name={'design-layer'}
                    size={'xl'}
                    color={Colors.BASE}
                />

                <div className={'flex flex-col'}>
                    <Text
                        weight={'semibold'}
                        size={'default'}
                    >
                        {t('cycleEvaluationSelectEvaluators.prioritySection.title')}
                    </Text>

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'mt-3'}
                    >
                        {t('cycleEvaluationSelectEvaluators.prioritySection.descriptionLine1')}
                    </Text>

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                    >
                        {t('cycleEvaluationSelectEvaluators.prioritySection.descriptionLine2')}
                    </Text>
                </div>
            </div>

            <PriorityBoard
                containers={containerConfigs}
                disabled={isReadOnly}
                onChange={handleChange}
                onAddAttribute={isReadOnly ? undefined : handleAddAttribute}
                onRemoveItem={!isReadOnly && onRemoveAttribute ? handleRemoveAttribute : undefined}
                // `title` is typed as required, but a criterion whose company attribute was
                // deleted or renamed comes back with no matching name, and only the id survives.
                getRemoveLabel={(attribute) =>
                    t('cycleEvaluationSelectEvaluators.prioritySection.removeCriterion', {
                        criterion: attribute.title || attribute.id,
                    })
                }
                dataTestId={`${dataTestId}-priority`}
            />
        </Card>
    );
};

export { PrioritySection };
export type { PrioritySectionProps };
